/**
 * Plotea self-service organization roster and invitation links
 * (`RM_API_PROFILE=plotea`). Contract: docs/architecture/plotea.md
 * ("Identity and roster"). Owners and admins manage the roster; any member
 * may leave; the `/invite` page uses the token flows.
 */
import { z } from 'zod'
import { api } from '../http'
import { ApiError } from '../errors'

const noRetry = { retryOnUnauthorized: false } as const

export const PloteaRoleSchema = z.enum(['owner', 'admin', 'member'])
export type PloteaRole = z.infer<typeof PloteaRoleSchema>

export const RosterMemberSchema = z.object({
  user_id: z.string(),
  name: z.string().nullable(),
  email: z.string(),
  image: z.string().nullable(),
  role: PloteaRoleSchema,
  joined_at: z.string(),
})
export type RosterMember = z.infer<typeof RosterMemberSchema>

export const RosterInvitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: PloteaRoleSchema,
  status: z.enum(['pending', 'expired']),
  expires_at: z.string(),
  last_sent_at: z.string().nullable(),
  created_at: z.string(),
})
export type RosterInvitation = z.infer<typeof RosterInvitationSchema>

export const RosterSchema = z.object({
  organization_id: z.string(),
  name: z.string(),
  slug: z.string(),
  viewer_role: PloteaRoleSchema,
  members: z.array(RosterMemberSchema),
  invitations: z.array(RosterInvitationSchema),
})
export type Roster = z.infer<typeof RosterSchema>

const InvitationSentSchema = z.object({
  invitation: RosterInvitationSchema,
  email_sent: z.boolean(),
})
export type InvitationSent = z.infer<typeof InvitationSentSchema>

const orgPath = (id: string) => `/organizations/${encodeURIComponent(id)}`

export const getRoster = (id: string) =>
  api.get(`${orgPath(id)}/roster`, RosterSchema)

export const inviteToOrganization = (
  id: string,
  email: string,
  role: PloteaRole,
) =>
  api.post(`${orgPath(id)}/invitations`, { email, role }, InvitationSentSchema)

export const resendInvitation = (id: string, invitationId: string) =>
  api.post(
    `${orgPath(id)}/invitations/${encodeURIComponent(invitationId)}/resend`,
    undefined,
    InvitationSentSchema,
  )

export const revokeInvitation = (id: string, invitationId: string) =>
  api.del(`${orgPath(id)}/invitations/${encodeURIComponent(invitationId)}`)

export const changeMemberRole = (
  id: string,
  userId: string,
  role: PloteaRole,
) => api.patch(`${orgPath(id)}/members/${encodeURIComponent(userId)}`, { role })

export const removeOrganizationMember = (id: string, userId: string) =>
  api.del(`${orgPath(id)}/members/${encodeURIComponent(userId)}`)

export const leaveOrganization = (id: string) =>
  api.post(`${orgPath(id)}/leave`)

/** Owner only; `confirmName` must equal the organization name. */
export const deleteOrganization = (id: string, confirmName: string) =>
  api.post(`${orgPath(id)}/delete`, { confirm_name: confirmName })

// --- Invitation links (`/invite?token=…`) ----------------------------------

export const InvitationLookupSchema = z.object({
  organization_name: z.string(),
  email: z.string(),
  role: PloteaRoleSchema,
  expires_at: z.string(),
})
export type InvitationLookup = z.infer<typeof InvitationLookupSchema>

export const lookupInvitation = (token: string) =>
  api.get(
    `/auth/invitations/lookup?token=${encodeURIComponent(token)}`,
    InvitationLookupSchema,
    noRetry,
  )

/** Sign-up from an invitation: the address comes from the invitation. */
export const registerWithInvitation = (
  token: string,
  name: string,
  password: string,
) => api.post('/auth/register', { token, name, password }, undefined, noRetry)

/** Sign-in link to the invited address. */
export const invitationMagicLink = (token: string) =>
  api.post('/auth/magic-link', { token }, undefined, noRetry)

const AcceptedSchema = z.object({ organization_id: z.string() })

export const acceptInvitationToken = (token: string) =>
  api.post('/organizations/invitations/accept-token', { token }, AcceptedSchema)

// --- Pending invitation kept across sign-in --------------------------------

const PENDING_KEY = 'plotea.pendingInvitation'
/** Invitation links stay valid for days; a kept token only for a sign-in. */
const PENDING_TTL_MS = 60 * 60 * 1000

/**
 * The `/invite` page keeps the token while the visitor signs in (password,
 * magic link in another tab, Google/Microsoft); the app accepts it once a
 * session exists. Storage can be unavailable (private mode): then only the
 * OAuth path (server cookie) and "my invitations" accept.
 */
export function keepPendingInvitation(token: string): void {
  try {
    window.localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ token, at: Date.now() }),
    )
  } catch {
    /* storage unavailable */
  }
}

export function takePendingInvitation(): string | null {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY)
    window.localStorage.removeItem(PENDING_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as { token?: unknown; at?: unknown }
    if (
      typeof value.token !== 'string' ||
      typeof value.at !== 'number' ||
      Date.now() - value.at > PENDING_TTL_MS
    )
      return null
    return value.token
  } catch {
    return null
  }
}

// --- Polish copy -----------------------------------------------------------

export const ROLE_LABEL: Record<PloteaRole, string> = {
  owner: 'Właściciel',
  admin: 'Administrator',
  member: 'Członek',
}

/** Polish message for a roster API error. */
export function rosterErrorMessage(error: unknown): string {
  const code = error instanceof ApiError ? error.serverCode : undefined
  const status = error instanceof ApiError ? error.status : undefined
  switch (code) {
    case 'last_owner':
      return 'Organizacja musi mieć co najmniej jednego właściciela.'
    case 'owner_required':
      return 'Tylko właściciel może zarządzać właścicielami.'
    case 'organization_admin_required':
      return 'Tę operację może wykonać tylko właściciel lub administrator.'
    case 'invitation_pending':
      return 'Zaproszenie na ten adres już czeka na przyjęcie. Możesz je wysłać ponownie.'
    case 'already_member':
      return 'Ta osoba już należy do organizacji.'
    case 'rate_limit_exceeded':
      return 'Zbyt wiele zaproszeń w krótkim czasie. Spróbuj ponownie później.'
    case 'subscription_active':
      return 'Organizacja ma aktywną subskrypcję. Najpierw ją anuluj.'
    case 'organization_not_deletable':
      return 'Tej organizacji nie można usunąć w Plotea.'
    case 'validation_error':
      return 'Sprawdź poprawność wpisanych danych.'
    case 'invitation_not_found':
    case 'member_not_found':
      return 'Nie znaleziono. Odśwież stronę.'
    default:
      return status === 403
        ? 'Brak uprawnień do tej operacji.'
        : 'Nie udało się wykonać operacji. Spróbuj ponownie.'
  }
}
